import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ReportTemplate } from '../../../models/api/reports/report';
import { CasePipe } from '../../../pipes/case.pipe';
import { HighlightPipe } from '../../../pipes/highlight.pipe';
import { BaseComponent } from '../../base/base.component';
import { PreviewButtonComponent } from '../../core/buttons/preview-button.component';
import { TagComponent } from '../../core/labels/tag.component';

@Component({
  selector: 'app-report',
  imports: [
    CasePipe,
    HighlightPipe,
    CommonModule,
    TagComponent,
    PreviewButtonComponent,
  ],
  template: ` <div
    class="report-card tw-border tw-rounded-xl tw-flex tw-flex-col tw-h-full tw-border-l-4 tw-min-w-0 tw-max-w-full tw-w-full"
    [ngClass]="{
      'tw-border-4 tw-border-[var(--sed-engaged-black-color)]': isSelected,
      'tw-border': !isSelected,
      'tw-cursor-pointer hover:tw-shadow-md hover:tw-border-[var(--sed-black-60-color)]':
        !disabled,
      'tw-opacity-50': disabled && !isSelected,
    }"
  >
    <div
      class="tw-flex tw-py-2 tw-px-2 sm:tw-px-4 tw-items-center tw-min-h-[60px] tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-gap-0"
    >
      <!-- The radio excludes the preview button: a radio must not contain focusable content. -->
      <div
        class="tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-gap-2 sm:tw-gap-0 tw-min-w-0 tw-grow"
        role="radio"
        [attr.aria-checked]="isSelected"
        [attr.aria-label]="report.title"
        [attr.aria-describedby]="descDomId + ' ' + typeDomId"
        [attr.aria-disabled]="disabled ? 'true' : null"
        [attr.tabindex]="tabIndex"
        [class.tw-cursor-pointer]="!disabled"
        (click)="onSelect()"
        (keydown.enter)="$event.preventDefault(); onEnter()"
        (keydown.space)="$event.preventDefault(); onSelect()"
        (keydown.arrowdown)="onArrow($event, 1)"
        (keydown.arrowright)="onArrow($event, 1)"
        (keydown.arrowup)="onArrow($event, -1)"
        (keydown.arrowleft)="onArrow($event, -1)"
      >
        <div
          class="tw-font-bold tw-min-w-0 tw-w-full tw-break-words sm:tw-w-[200px] sm:tw-shrink-0 sm:tw-pr-2"
          [innerHTML]="report.title | highlight: filterValue"
        ></div>
        <div
          [id]="descDomId"
          class="tw-text-sm tw-text-balance tw-grow tw-min-w-0 tw-w-full tw-break-words sm:tw-pr-2"
          [innerHTML]="
            report.description.replace(' .', '') | highlight: filterValue
          "
        ></div>
      </div>
      <div
        class="tw-flex tw-justify-end tw-items-center tw-gap-2 tw-w-full sm:tw-w-auto sm:tw-basis-[180px] sm:tw-min-w-[180px] tw-shrink-0"
      >
        <span [id]="typeDomId" class="tw-sr-only">{{
          report.typeTag | case: 'sentence'
        }}</span>
        <app-tag type="Grey60" aria-hidden="true">{{
          report.typeTag | case: 'sentence'
        }}</app-tag>
        <app-preview-button
          [src]="'assets/images/reports/examples/' + report.previewSrc"
          tooltip="Preview report"
          [alt]="report.title + ' preview'"
        />
      </div>
    </div>
  </div>`,
  styles: `
    .report-card:has([role='radio']:focus-visible) {
      outline: 2px solid var(--sed-engaged-black-color);
      outline-offset: -2px;
    }

    .report-card [role='radio']:focus-visible {
      outline: none;
    }
  `,
})
export class ReportComponent extends BaseComponent {
  @Input()
  report: ReportTemplate;

  @Input()
  control: FormControl<ReportTemplate>;

  @Input()
  filterValue: string;

  @Input()
  disabled = false;

  /**
   * True for the first card in the radiogroup. The radio pattern exposes a single
   * tab stop, so the first card is that stop while no report is selected.
   */
  @Input()
  first = false;

  @Output()
  doubleEnter = new EventEmitter<void>();

  private lastEnterTime = 0;
  private readonly doubleEnterThreshold = 400;

  get tabIndex(): number {
    return -1;
  }

  get descDomId(): string {
    return `report-desc-${this.report.id}`;
  }

  get typeDomId(): string {
    return `report-type-${this.report.id}`;
  }

  get isSelected(): boolean {
    return this.control.value?.id === this.report.id;
  }

  onSelect(): void {
    if (!this.disabled) {
      this.control.setValue(this.report);
    }
  }

  /**
   * Radio pattern: arrow keys move focus within the radiogroup and select the
   * radio they land on, unless the group is read only.
   */
  onArrow(event: Event, offset: number): void {
    event.preventDefault();

    const current = event.currentTarget as HTMLElement;
    const group = current.closest('[role="radiogroup"]');
    if (!group) {
      return;
    }

    const radios = Array.from(
      group.querySelectorAll<HTMLElement>('[role="radio"]'),
    );
    const index = radios.indexOf(current);
    if (index === -1) {
      return;
    }

    const next = radios[(index + offset + radios.length) % radios.length];
    next?.focus();
    if (!this.disabled) {
      next?.click();
    }
  }

  onEnter(): void {
    if (this.disabled) return;

    const now = Date.now();
    if (now - this.lastEnterTime < this.doubleEnterThreshold) {
      this.doubleEnter.emit();
      this.lastEnterTime = 0;
    } else {
      this.onSelect();
      this.lastEnterTime = now;
    }
  }
}
